import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import ApiInstance from 'src/helpers/apiInstance';
import actions from './actions';
import { sendRequest } from '../../../../../../helpers/saga';
import { logger } from '../../../../../../helpers/logger';
import notifications from '../../../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../../../helpers/core';
import { t } from '../../../../../../i18n';

const api = new ApiInstance('subunits/:id/managers/:managerId');
const createManagerApi = new ApiInstance('subunits/:id/managers');
const managersFormDataApi = new ApiInstance('subunits/:id/managers/form_data');
const convertToApplicantApi = new ApiInstance('subunits/:id/managers/:managerId/convert_to_worker');

export const getValidationErrors = result =>
  result?.data?.errors?.data?.validations ?? result?.data?.validations ?? result ?? {};

function* loadData({ payload }) {
  try {
    const { id, managerId, isEdit } = payload;

    const result = yield call(sendRequest, api.get, { pathParams: { id, managerId } }, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      const { data } = result;

      yield put(actions.loadDataSuccess({ data: { ...data, id, managerId } }));

      if (isEdit) {
        yield put(actions.edit({ id, manager: { ...data } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateManager({ payload }) {
  try {
    const { editForm, id, managerId } = payload;

    const propsArray = Object.values(editForm);
    const manager = keyByProperty(propsArray, 'name', prop => prop.value);
    const { avatar_url, avatar, departments, ...otherProps } = manager;
    const saveManagerObject = {
      department_ids: departments.map(item => item.id),
      ...otherProps,
    };

    if (avatar_url && !avatar_url.startsWith('http')) {
      saveManagerObject.avatar = avatar;
    }

    const result = yield call(
      sendRequest,
      api.put,
      {
        data: { manager: saveManagerObject },
        pathParams: {
          id,
          managerId,
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.status === 200) {
        yield put(actions.saveSuccess({ data: manager }));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.edited_successfully', {
              item: t('dash.subUnits.subUnit.managers.manager_title'),
            }),
          }),
        );
        yield put(push(`/subUnits/${id}/managers/${managerId}`));
      } else {
        yield put(actions.saveError({ data: getValidationErrors(result) }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteManager({ payload }) {
  try {
    const { subUnitId, managerId } = payload;

    const result = yield call(
      sendRequest,
      api.delete,
      { pathParams: { id: subUnitId, managerId } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', {
            item: t('dash.subUnits.subUnit.managers.manager_title'),
          }),
        }),
      );
      yield put(push(`/subUnits/${subUnitId}?tab[]=managers`));
    } else if (result?.messages) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: result.messages,
        }),
      );
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* createManager({ payload }) {
  try {
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const manager = keyByProperty(propsArray, 'name', prop => prop.value);

    const { avatar_url, departments, ...otherProps } = manager;
    const saveManagerObject = {
      avatar: avatar_url,
      department_ids: departments.map(item => item.id),
      ...otherProps,
    };

    const result = yield call(
      sendRequest,
      createManagerApi.post,
      { data: { manager: saveManagerObject }, pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      const { data } = result;

      if (data) {
        yield put(actions.saveSuccess({ data: { ...manager, id: data.id } }));
        yield put(actions.loadData({ id, managerId: data.id }));
        yield put(push(`/subUnits/${id}/managers/${data.id}`));
      } else {
        yield put(actions.saveError({ data: getValidationErrors(result) }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const [id] = payload.id;

    const result = yield call(
      sendRequest,
      managersFormDataApi.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      const { data } = result;

      yield put(actions.loadFormData({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* convertToApplicant({ payload }) {
  try {
    const { id, managerId } = payload;

    const result = yield call(
      sendRequest,
      convertToApplicantApi.post,
      { pathParams: { id, managerId } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(push(`/applicants/${result.data.id}`));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.subUnits.subUnit.managers.converted_to_applicant_message'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateManager),
    takeEvery(actions.DELETE, deleteManager),
    takeEvery(actions.ADD, createManager),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.CREATE, loadFormData),
    takeEvery(actions.CONVERT_TO_APPLICANT, convertToApplicant),
  ]);
}
